import React from 'react';
import { PageToolbarProps } from './types';
import styled from 'styled-components';
import { Button, Menu } from '@companycam/slab-web';
import CoverPage from './assets/cover-page--title-with-logo.png';
import HorizontalImageLeft from './assets/horizontal-image-left.png';
import HorizontalImageRight from './assets/horizontal-image-right.png';
import VerticalTwoColumns from './assets/vertical-two-columns.png';
import VerticalThreeColumns from './assets/vertical-three-columns.png';
import { LayoutOption } from './LayoutOption';
import { translate } from 'javascripts/translations/i18n';

const StyledMenu = styled(Menu)`
  margin-top: var(--cc_size_spacing_xs);
  margin-bottom: var(--cc_size_spacing_xs);
`;

const StyledButton = styled(Button)`
  &.toolbar-layout-trigger {
    padding: 0;
    height: var(--cc_size_avatar_m);
    width: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Title = styled.p`
  color: var(--Text-Default, var(--cc_color_text_default));
  font-family: Averta;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 22.4px;
  margin: var(--cc_size_spacing_s) 0px var(--cc_size_spacing_m)
    var(--cc_size_spacing_m);
`;

const LayoutOptionsHeading = styled.p`
  color: var(--Text-Default, var(--cc_color_text_default));
  font-family: Averta;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 19.6px;
  margin-bottom: 0px;
`;

const LayoutOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_m);
  height: 373px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0px var(--cc_size_spacing_m) var(--cc_size_spacing_m)
    var(--cc_size_spacing_m);
`;

const CoverPageLayoutOptions = styled.div`
  display: flex;
  flex-direction: row;

  & button {
    max-width: 67%;
  }
`;

export type LayoutMenuProps = {
  editor: PageToolbarProps['editor'];
};

export function LayoutMenu(props: LayoutMenuProps) {
  const { editor } = props;

  // TODO: get project data from /v3/projects/{id}
  const projectData = {};

  const handleInsertHorizontalLeftLayout = () => {
    editor.chain().insertHorizontalLayout({ direction: 'left' }).run();
  };

  const handleInsertHorizontalRightLayout = () => {
    editor.chain().insertHorizontalLayout({ direction: 'right' }).run();
  };

  const handleInsertVerticalTwoColumnLayout = () => {
    editor.chain().insertVerticalLayout({ columns: 2 }).run();
  };

  const handleInsertVerticalThreeColumnLayout = () => {
    editor.chain().insertVerticalLayout({ columns: 3 }).run();
  };

  const handleInsertCoverPage = () => {
    editor.chain().insertCoverPage(projectData).run();
  };

  return (
    <StyledMenu
      placement="top"
      tooltip={translate('pages.toolbar.textAndImageLayouts')}
      triggerButton={
        <StyledButton
          disabled={!editor.can().insertHorizontalLayout({ direction: 'left' })}
          id="pages-toolbar-layout-trigger"
          className="toolbar-layout-trigger"
          data-testid="pages__toolbar-layout-menu-trigger"
          design="minimal"
        >
          <i className="mdi mdi-plus-circle-outline" />
          <i className="mdi mdi-chevron-down" />
        </StyledButton>
      }
    >
      <Title data-testid="pages__toolbar-layout-popover-title">
        {translate('pages.toolbar.layoutPopoverTitle')}
      </Title>
      <LayoutOptions>
        <LayoutOptionsHeading>
          {translate('pages.toolbar.layoutsPopoverCoverPage')}
        </LayoutOptionsHeading>
        <CoverPageLayoutOptions>
          <LayoutOption
            onClick={handleInsertCoverPage}
            disabled={!editor.can().insertCoverPage({})}
            data-testid="pages__toolbar-layout-popover-cover-page"
            src={CoverPage}
            accessibilityLabel={translate(
              'pages.toolbar.layoutsPopoverCoverPageLabel',
            )}
          />
        </CoverPageLayoutOptions>
        <LayoutOptionsHeading>
          {translate('pages.toolbar.layoutsPopoverHorizontal')}
        </LayoutOptionsHeading>
        <LayoutOption
          onClick={handleInsertHorizontalLeftLayout}
          data-testid="pages__toolbar-layout-popover-layout-option-image-left"
          src={HorizontalImageLeft}
          accessibilityLabel={translate(
            'pages.toolbar.layoutsPopoverImageLeftLabel',
          )}
        />
        <LayoutOption
          onClick={handleInsertHorizontalRightLayout}
          data-testid="pages__toolbar-layout-popover-layout-option-image-right"
          src={HorizontalImageRight}
          accessibilityLabel={translate(
            'pages.toolbar.layoutsPopoverImageRightLabel',
          )}
        />
        <LayoutOptionsHeading>
          {translate('pages.toolbar.layoutsPopoverVertical')}
        </LayoutOptionsHeading>
        <LayoutOption
          onClick={handleInsertVerticalTwoColumnLayout}
          data-testid="pages__toolbar-layout-popover-layout-option-two-column"
          src={VerticalTwoColumns}
          accessibilityLabel={translate(
            'pages.toolbar.layoutsPopoverTwoColumnLabel',
          )}
        />
        <LayoutOption
          onClick={handleInsertVerticalThreeColumnLayout}
          data-testid="pages__toolbar-layout-popover-layout-option-three-column"
          src={VerticalThreeColumns}
          accessibilityLabel={translate(
            'pages.toolbar.layoutsPopoverThreeColumnLabel',
          )}
        />
      </LayoutOptions>
    </StyledMenu>
  );
}
